// ─────────────────────────────────────────────────────────────────────────────
// utils/diagnose.ts — das Diagnose-Protokoll der Verwaltungsoberflaeche
// (T-M21.1, REQ-OPS-055 (a) bis (g), REQ-SEC-021, REQ-OV-002, REQ-OV-005)
//
// ── WOFUER ──────────────────────────────────────────────────────────────────
// Beim Felddefekt vom 2026-08-17/18 blieb die Oberflaeche stehen, ohne etwas zu
// melden. Beim naechsten Auftreten soll die Konsole auf dem Rechner des Nutzers
// den Uebeltaeter benennen. Dazu braucht es genau eines: EINE Form und EIN
// Praefix — eine Konsole mit vier Eigenbauten ist im Feld nicht filterbar
// (REQ-OPS-055 (a)).
//
// ── DIE FORM, und sie ist genau so gewollt ─────────────────────────────────
//   console.<stufe>(`${DIAGNOSE_PREFIX} <ISO-Zeitstempel> <Ereignisname>`, kontext)
// Das Praefix steht hier bewusst als Konstante und nicht ausgeschrieben: es
// kommt im ganzen Quellbaum GENAU EINMAL vor, und das haelt ein Waechter.
// Der Kontext ist das ZWEITE Argument und bleibt damit ein Objekt: in den
// Entwicklerwerkzeugen aufklappbar, im Speicher nicht zu einer Zeichenkette
// zerfallen. Wer ihn in die Zeichenkette hineinformatiert, nimmt genau das weg.
//
// ── VIER EREIGNISKLASSEN, alle vier verbindlich (REQ-OPS-055 (b)) ──────────
//   (1) Anmeldezustand    → `diagnoseAuthState`        (`auth.<…>`)
//   (2) Sitzungsentscheid → `diagnoseSessionDecision`  (`session.<…>`)
//   (3) Abo-Lebenslauf    → `diagnoseSubscription`     (`abo.<…>`)
//   (4) Start-Schritt     → `diagnoseBootstrapStep`    (`start.<…>`, mit Dauer)
// DIESER TASK STELLT SIE BEREIT UND RUFT SIE NICHT. Die Rufe liegen bei
// T-M21.2 (Start), T-M21.3 (Sitzung), T-M21.4 / T-M21.7 (Abos) und T-M21.6
// (Dienst-Arbeiter). Wer hier schon protokolliert, nimmt jenen Tasks ihren
// Gegenstand.
//
// ── IM PRODUKTIVBAU EINGESCHALTET (REQ-OPS-055 (c)) ────────────────────────
// Es gibt KEINEN Umgebungsschalter — kein `import.meta.env`, kein `DEV`, kein
// `PROD`. Der Zweck ist die Suche auf dem Rechner des Nutzers; ein Schalter,
// der im Feld aus ist, hilft nie. Die Stufen: `info` fuer Uebergaenge, `warn`
// fuer Behebbares, `error` fuer Endgueltiges.
//
// ── DIE SPERRE IST GEBAUT, NICHT ZUGESICHERT (REQ-OPS-055 (d), REQ-SEC-021) ─
// Der Kontext wird vor der Ausgabe auf die ABSCHLIESSENDE Feldliste
// `ERLAUBTE_KONTEXTFELDER` verengt. Das ist eine Positivliste und keine
// Sperrliste: was nicht darin steht, faellt weg — `payload`, `screenKey`,
// `token`, `password`, Beleginhalte und personenbezogene Angaben ausser
// Benutzer- und Mandantenkennung gelangen deshalb auch dann nicht in die
// Ausgabe, wenn ein Aufrufer sie mitgibt. Eine Sperrliste haette nur die
// Namen erwischt, an die jemand gedacht hat.
//
// ── GRENZEN, benannt ───────────────────────────────────────────────────────
//   • DIAGNOSE IST KEIN NACHWEIS (REQ-OPS-055 (g)). Eine Zeile sagt, was
//     geschah — nicht, dass es richtig war. Sie ersetzt weder eine Zusicherung
//     noch das Ausgangstor.
//   • Es wird NICHTS nach Firestore geschrieben und kein Statuswert beruehrt
//     (REQ-OPS-055 (f), REQ-OV-002). Dieses Modul importiert kein Firebase.
//   • Die bestehenden Ausgaben (`[AuthStore]`, `[PrinterStore]`,
//     `[PrintJobService]`, `[CompanyStore]`) werden NICHT abgeloest — das waere
//     ein zweiter Task und wuerde diesen verzoegern, der gebraucht wird.
//   • Protokolliert werden UEBERGAENGE, nicht Zustaende (REQ-OPS-055 (e)):
//     keine Zeile je Dokument, je Schnappschuss oder je Bild. Diese Grenze
//     haelt das Modul nicht — sie liegt bei der rufenden Stelle.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Das Praefix. **Diese Zeile ist die einzige Stelle im Quellbaum, an der die
 * Zeichenkette gebildet wird** (REQ-OPS-055 (a)); ein Waechter haelt das
 * (T-M21.8). Wer sie anderswo hinschreibt, spaltet den Filter im Feld.
 */
export const DIAGNOSE_PREFIX = '[PQ-Diagnose]';

/** Die drei Stufen nach REQ-OPS-055 (c). */
export type DiagnoseStufe = 'info' | 'warn' | 'error';

/**
 * Die abschliessende Feldliste (REQ-OPS-055 (d)).
 *
 * Aufgenommen ist nur, was beim naechsten Feldauftreten wirklich gesucht wird.
 * Ausdruecklich erlaubte personenbezogene Angaben sind allein die
 * Benutzerkennung (`userId`) und die Mandantenkennung (`companyId`); alles
 * uebrige hier ist technisch.
 *
 * **Erweiterung ist ein Entscheid, keine Beilaeufigkeit:** wer ein Feld
 * ergaenzt, verantwortet, dass darin nie ein Geheimnis, eine Nutzlast oder ein
 * Beleginhalt landet.
 */
export const ERLAUBTE_KONTEXTFELDER = [
  // Wer und wo (die zwei erlaubten Kennungen plus technische Zuordnung)
  'userId',
  'companyId',
  'locationId',
  'printerId',
  'printserverId',
  // Abo-Lebenslauf (REQ-OPS-053 (a), (b))
  'collection',
  'path',
  'attempt',
  'delayMs',
  // Fehler (REQ-OPS-052 (c): ein Fehlerzustand ohne Code ist keiner)
  'code',
  'message',
  // Start-Schritte mit Dauer (REQ-OPS-052, REQ-OPS-055 (b))
  'step',
  'durationMs',
  'phase',
  // Anmeldezustand
  'signedIn',
  // Sitzungsentscheide (REQ-OPS-056 (c))
  'loginAt',
  'ageMs',
  'verdict',
  'source',
  // Dienst-Arbeiter (REQ-OPS-057 (e))
  'scope',
  'scriptUrl',
  // Aggregat statt Einzelzeile (REQ-OPS-055 (e))
  'count',
] as const;

/** Ein Feldname aus der abschliessenden Liste. */
export type ErlaubtesKontextfeld = (typeof ERLAUBTE_KONTEXTFELDER)[number];

/**
 * Der Kontext, wie ihn eine rufende Stelle uebergibt.
 *
 * Die Indexsignatur ist Absicht: ein Aufrufer darf ein bestehendes Objekt
 * hineinreichen, ohne es vorher zu putzen — die Verengung macht das Modul.
 * Nur eine Sperre, die auch dann haelt, wenn ihr etwas Verbotenes uebergeben
 * wird, ist eine Sperre (REQ-OPS-055 (d)).
 */
export type DiagnoseKontext = Partial<Record<ErlaubtesKontextfeld, unknown>> & {
  [feld: string]: unknown;
};

/** Der ausgegebene Kontext: verengt, flach, ohne Fremdwerte. */
export type DiagnoseAusgabeKontext = Partial<Record<ErlaubtesKontextfeld, string | number | boolean | null>>;

/**
 * Obergrenze fuer Zeichenketten im Kontext.
 *
 * `message` ist das einzige Feld mit freiem Text (der Firebase-Meldungstext,
 * den REQ-OPS-052 (c) ausdruecklich verlangt). Die Kappung begrenzt die Menge
 * (REQ-OPS-055 (e)) und nimmt einer versehentlich durchgereichten laengeren
 * Zeichenkette die Wucht — sie ersetzt die Positivliste nicht, sie ergaenzt sie.
 */
const MAX_TEXTLAENGE = 300;

/** Zeitstempel in ISO-8601 mit Millisekunden (REQ-OPS-055 (a)). */
function jetztIso(): string {
  return new Date().toISOString();
}

/**
 * Ein einzelner Wert, so weit er ausgegeben werden darf.
 *
 * Durch kommen nur flache Grundwerte. Ein Objekt oder ein Feld faellt weg —
 * beides ist der Weg, auf dem sonst ein ganzer `payload` unter einem erlaubten
 * Feldnamen mitkaeme. Ein `Date` wird zur ISO-Zeichenkette, weil ein
 * Zeitstempel in der Diagnose der Normalfall ist.
 */
function verengeWert(wert: unknown): string | number | boolean | null | undefined {
  if (wert === null) return null;
  if (typeof wert === 'boolean') return wert;
  if (typeof wert === 'number') return Number.isFinite(wert) ? wert : String(wert);
  if (typeof wert === 'string') {
    return wert.length > MAX_TEXTLAENGE ? `${wert.slice(0, MAX_TEXTLAENGE)}…` : wert;
  }
  if (wert instanceof Date) return wert.toISOString();
  // Objekte, Felder, Funktionen, `undefined`, Symbole: nicht ausgegeben.
  return undefined;
}

/**
 * Die Sperre (REQ-OPS-055 (d)): verengt einen beliebigen Kontext auf die
 * abschliessende Feldliste und auf flache Grundwerte.
 *
 * Exportiert, damit sie **einzeln** vorgefuehrt werden kann — ein Test, der die
 * Sperre nur durch die Ausgabe hindurch sieht, belegt sie schwaecher.
 */
export function verengeKontext(kontext: DiagnoseKontext | undefined): DiagnoseAusgabeKontext {
  const verengt: Record<string, string | number | boolean | null> = {};
  if (!kontext || typeof kontext !== 'object') return verengt;
  for (const feld of ERLAUBTE_KONTEXTFELDER) {
    if (!Object.prototype.hasOwnProperty.call(kontext, feld)) continue;
    const wert = verengeWert((kontext as Record<string, unknown>)[feld]);
    if (wert === undefined) continue;
    verengt[feld] = wert;
  }
  return verengt;
}

/** Die Konsolenfunktion zur Stufe (REQ-OPS-055 (c)). */
function konsole(stufe: DiagnoseStufe): typeof console.info {
  if (stufe === 'error') return console.error.bind(console);
  if (stufe === 'warn') return console.warn.bind(console);
  return console.info.bind(console);
}

/**
 * Die gemeinsame Bildungsstelle. **Jede Zeile des Diagnose-Protokolls entsteht
 * hier** — die vier Klassen darunter sind benannte Einstiege, keine zweiten
 * Formen.
 *
 * Der Regelweg sind die vier Klassen aus REQ-OPS-055 (b). Diese Funktion ist
 * fuer die wenigen Zeilen offen, die in keine von ihnen faellt (etwa die eine
 * Zeile nach der Entfernung eines Alt-Dienst-Arbeiters, REQ-OPS-057 (e)) —
 * nicht als Umgehung der Klassen.
 *
 * **Sie wirft nie.** Sie wird in Fehlerpfaden gerufen; eine Ausgabe, die ihren
 * eigenen Aufrufer zu Fall bringt, waere schlimmer als keine.
 */
export function diagnose(stufe: DiagnoseStufe, ereignis: string, kontext?: DiagnoseKontext): void {
  try {
    konsole(stufe)(`${DIAGNOSE_PREFIX} ${jetztIso()} ${ereignis}`, verengeKontext(kontext));
  } catch {
    // Bewusst still: das Protokoll ist Diagnose, kein Nachweis (REQ-OPS-055 (g)).
  }
}

/**
 * (1) Anmeldezustand — Kennung an/ab, Fehler beim Erneuern des Ausweises
 * (REQ-OPS-055 (b)). Ereignisnamen tragen das Praefix `auth.`.
 *
 * Beispiele fuer T-M21.2: `diagnoseAuthState('signedIn', { userId })`,
 * `diagnoseAuthState('tokenRefreshFailed', { code }, 'error')`.
 */
export function diagnoseAuthState(
  ereignis: string,
  kontext?: DiagnoseKontext,
  stufe: DiagnoseStufe = 'info',
): void {
  diagnose(stufe, `auth.${ereignis}`, kontext);
}

/**
 * (2) Sitzungsentscheid — gelesener Wert, Alter, Verdikt und welche der zwei
 * Stellen entschieden hat (REQ-OPS-055 (b), REQ-OPS-056 (c)).
 *
 * Der Fall „kein Zeitstempel" ist ausdruecklich mitgemeint: `loginAt: null`
 * gehoert in den Kontext und faellt nicht etwa weg (deshalb laesst
 * `verengeWert` `null` durch — ein weggelassenes Feld waere hier gerade der
 * fehlende Befund).
 */
export function diagnoseSessionDecision(
  ereignis: string,
  kontext?: DiagnoseKontext,
  stufe: DiagnoseStufe = 'info',
): void {
  diagnose(stufe, `session.${ereignis}`, kontext);
}

/**
 * (3) Abo-Lebenslauf — abonniert, Fehler mit Code, Wiederaufnahme mit
 * Versuchsnummer, abgemeldet (REQ-OPS-055 (b), REQ-OPS-053 (a)/(b)).
 *
 * Der Kontext traegt Collection bzw. Dokument (`collection`/`path`), die
 * Mandantenkennung (`companyId`) und im Fehlerfall den `code`; die
 * Wiederaufnahme zusaetzlich `attempt` und `delayMs`.
 */
export function diagnoseSubscription(
  ereignis: string,
  kontext?: DiagnoseKontext,
  stufe: DiagnoseStufe = 'info',
): void {
  diagnose(stufe, `abo.${ereignis}`, kontext);
}

/**
 * (4) Start-Schritt MIT DAUER in Millisekunden (REQ-OPS-055 (b), REQ-OPS-052).
 *
 * Die Dauer ist ein eigener Parameter und kein beliebiges Kontextfeld, weil
 * genau sie den Unterschied traegt, um den es geht: ohne sie ist beim naechsten
 * Auftreten nicht unterscheidbar, ob ein Schritt FEHLSCHLUG oder HAENGT. Sie
 * gewinnt gegen ein gleichnamiges Feld im Kontext.
 */
export function diagnoseBootstrapStep(
  schritt: string,
  durationMs: number,
  kontext?: DiagnoseKontext,
  stufe: DiagnoseStufe = 'info',
): void {
  diagnose(stufe, `start.${schritt}`, { ...kontext, step: schritt, durationMs });
}
